import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ChatProvider } from "./context/ChatContext";
import { TasksProvider } from "./context/TasksContext";
import { RefreshProvider } from "./context/RefreshContext";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import WelcomeModal from "./components/WelcomeModal";
import ChatWidget from "./components/ChatWidget";

// Critical (eager): LoginPage + Dashboard for first paint
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";

// Lazy chunks — split into per-route bundles
const TwoFactorPage = lazy(() => import("./pages/TwoFactorPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const ClientDetailPage = lazy(() => import("./pages/ClientDetailPage"));
const TourOperatorsPage = lazy(() => import("./pages/TourOperatorsPage"));
const BookingsPage = lazy(() => import("./pages/BookingsPage"));
const PaymentsPage = lazy(() => import("./pages/PaymentsPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const WorkHoursPage = lazy(() => import("./pages/WorkHoursPage"));
const LeavesPage = lazy(() => import("./pages/LeavesPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const CommissionsPage = lazy(() => import("./pages/CommissionsPage"));
const PromotionsPage = lazy(() => import("./pages/PromotionsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const BranchesPage = lazy(() => import("./pages/BranchesPage"));
const ClientPortalPage = lazy(() => import("./pages/ClientPortalPage"));
const SetPasswordPage = lazy(() => import("./pages/SetPasswordPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const VouchersPage = lazy(() => import("./pages/VouchersPage"));
const PublicVoucherPage = lazy(() => import("./pages/PublicVoucherPage"));
const VoucherStudioPage = lazy(() => import("./pages/VoucherStudioPage"));
const CashReportPage = lazy(() => import("./pages/CashReportPage"));
const ChatHistoryPage = lazy(() => import("./pages/ChatHistoryPage"));
const FactoryResetPage = lazy(() => import("./pages/FactoryResetPage"));
const BranchDashboardPage = lazy(() => import("./pages/BranchDashboardPage"));
const OwnerDashboardPage = lazy(() => import("./pages/OwnerDashboardPage"));
const FranchiseSettlementsPage = lazy(() => import("./pages/FranchiseSettlementsPage"));
const TeamCommissionsPage = lazy(() => import("./pages/TeamCommissionsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const GroupTripsPage = lazy(() => import("./pages/GroupTripsPage"));
const GroupTripDetailPage = lazy(() => import("./pages/GroupTripDetailPage"));
const VaultPage = lazy(() => import("./pages/VaultPage"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const ContractsPage = lazy(() => import("./pages/ContractsPage"));
const FinancePage = lazy(() => import("./pages/FinancePage"));
const EmployeeStatsPage = lazy(() => import("./pages/EmployeeStatsPage"));
const CampaignsPage = lazy(() => import("./pages/CampaignsPage"));
const SettlementsPage = lazy(() => import("./pages/SettlementsPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const NotificationPreferencesPage = lazy(() => import("./pages/NotificationPreferencesPage"));
const CashReconciliationPage = lazy(() => import("./pages/CashReconciliationPage"));
const TrainingsPage = lazy(() => import("./pages/TrainingsPage"));
const DemoPage = lazy(() => import("./pages/DemoPage"));
const SystemStatusPage = lazy(() => import("./pages/SystemStatusPage"));
const SystemMetricsPage = lazy(() => import("./pages/SystemMetricsPage"));
const CostsPage = lazy(() => import("./pages/CostsPage"));
const OcrReviewInboxPage = lazy(() => import("./pages/OcrReviewInboxPage"));
const ArchiveCenterPage = lazy(() => import("./pages/ArchiveCenterPage"));
const InvoicesQAPage = lazy(() => import("./pages/InvoicesQAPage"));
const InvoiceFormPage = lazy(() => import("./pages/InvoiceFormPage"));
const PublicInvoicePage = lazy(() => import("./pages/PublicInvoicePage"));
const MyCommissionPage = lazy(() => import("./pages/MyCommissionPage"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const KsefPage = lazy(() => import("./pages/KsefPage"));

// Customer Portal v2 (luxury dark) - separate chunks (unauthenticated, no AuthProvider needed)
const PortalLoginPage = lazy(() => import("./portal/PortalLoginPage"));
const PortalMagicVerifyPage = lazy(() => import("./portal/PortalMagicVerifyPage"));
const PortalDashboardPage = lazy(() => import("./portal/PortalDashboardPage"));
const PortalInvoiceListPage = lazy(() => import("./portal/PortalInvoiceListPage"));
const PortalInvoiceFormPage = lazy(() => import("./portal/PortalInvoiceFormPage"));
const PortalDocumentsPage = lazy(() => import("./portal/PortalDocumentsPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-[60vh] text-muted-foreground" data-testid="page-loader">
    <div className="animate-pulse text-sm">Ładowanie strony…</div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-muted-foreground">Ładowanie...</div>;
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-muted-foreground">Ładowanie...</div>;
  if (!user) return <Navigate to="/login" />;
  return isAdmin ? children : <Navigate to="/" />;
};

const RoleRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-muted-foreground">Ładowanie...</div>;
  if (!user) return <Navigate to="/login" />;
  return roles.includes(user.role) ? children : <Navigate to="/" />;
};

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RefreshProvider defaultInterval={30000}>
          <ChatProvider>
            <TasksProvider>
            <Toaster richColors position="top-right" />
            <WelcomeModal />
            <ChatWidget />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/2fa" element={<TwoFactorPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/panel-klienta" element={<Navigate to="/portal/login" replace />} />
              <Route path="/ustaw-haslo" element={<SetPasswordPage />} />
              <Route path="/reset-password" element={<SetPasswordPage />} />
              <Route path="/zapomnialem-hasla" element={<ForgotPasswordPage />} />
              <Route path="/polityka-prywatnosci" element={<PrivacyPolicyPage />} />
              <Route path="/public/invoice/:token" element={<PublicInvoicePage />} />
              <Route path="/voucher/:number" element={<PublicVoucherPage />} />
              <Route path="/vouchers/studio" element={<ProtectedRoute><VoucherStudioPage /></ProtectedRoute>} />

              {/* Customer Portal v2 (Luxury Dark) — unauthenticated */}
              <Route path="/portal" element={<PortalLoginPage />} />
              <Route path="/portal/login" element={<PortalLoginPage />} />
              <Route path="/portal/magic/:token" element={<PortalMagicVerifyPage />} />
              <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
              <Route path="/portal/invoice-requests" element={<PortalInvoiceListPage />} />
              <Route path="/portal/invoice-requests/new" element={<PortalInvoiceFormPage />} />
              <Route path="/portal/invoice-requests/:id" element={<PortalInvoiceFormPage />} />
              <Route path="/portal/documents" element={<PortalDocumentsPage />} />
              <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<DashboardPage />} />
                <Route path="my-account" element={<MyAccountPage />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="clients/:id" element={<ClientDetailPage />} />
                <Route path="tour-operators" element={<RoleRoute roles={['admin','kierownik']}><TourOperatorsPage /></RoleRoute>} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="vouchers" element={<VouchersPage />} />
                <Route path="invoices" element={<RoleRoute roles={['admin','kierownik']}><InvoicesPage /></RoleRoute>} />
                <Route path="invoices/new" element={<AdminRoute><InvoiceFormPage /></AdminRoute>} />
                <Route path="invoices/edit/:id" element={<AdminRoute><InvoiceFormPage /></AdminRoute>} />
                <Route path="work-hours" element={<WorkHoursPage />} />
                <Route path="leaves" element={<LeavesPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="cash-report" element={<CashReportPage />} />
                <Route path="chat-history" element={<ChatHistoryPage />} />
                <Route path="factory-reset" element={<RoleRoute roles={['admin']}><FactoryResetPage /></RoleRoute>} />
                <Route path="branch-dashboard" element={<RoleRoute roles={['admin', 'branch_admin', 'kierownik']}><BranchDashboardPage /></RoleRoute>} />
                <Route path="owner-dashboard" element={<AdminRoute><OwnerDashboardPage /></AdminRoute>} />
                <Route path="franchise-settlements" element={<RoleRoute roles={['admin', 'branch_admin', 'kierownik']}><FranchiseSettlementsPage /></RoleRoute>} />
                <Route path="team-commissions" element={<RoleRoute roles={['admin', 'branch_admin', 'kierownik']}><TeamCommissionsPage /></RoleRoute>} />
                <Route path="group-trips" element={<GroupTripsPage />} />
                <Route path="group-trips/:id" element={<GroupTripDetailPage />} />
                <Route path="vault" element={<VaultPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="contracts" element={<RoleRoute roles={['admin','kierownik']}><ContractsPage /></RoleRoute>} />
                <Route path="finanse" element={<RoleRoute roles={['admin','branch_admin','kierownik']}><FinancePage /></RoleRoute>} />
                <Route path="employee-stats" element={<EmployeeStatsPage />} />
                <Route path="campaigns" element={<RoleRoute roles={['admin','kierownik']}><CampaignsPage /></RoleRoute>} />
                <Route path="settlements" element={<SettlementsPage />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="ustawienia-powiadomien" element={<NotificationPreferencesPage />} />
                <Route path="rozliczenia-gotowki" element={<CashReconciliationPage />} />
                <Route path="szkolenia" element={<TrainingsPage />} />
                <Route path="reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
                <Route path="commissions" element={<AdminRoute><CommissionsPage /></AdminRoute>} />
                <Route path="moja-prowizja" element={<MyCommissionPage />} />
                <Route path="audit-log" element={<AdminRoute><AuditLogPage /></AdminRoute>} />
                <Route path="ksef" element={<AdminRoute><KsefPage /></AdminRoute>} />
                <Route path="promotions" element={<AdminRoute><PromotionsPage /></AdminRoute>} />
                <Route path="branches" element={<AdminRoute><BranchesPage /></AdminRoute>} />
                <Route path="users" element={<AdminRoute><UsersPage /></AdminRoute>} />
                <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
                <Route path="admin/system-status" element={<AdminRoute><SystemStatusPage /></AdminRoute>} />
                <Route path="admin/system-metrics" element={<AdminRoute><SystemMetricsPage /></AdminRoute>} />
                <Route path="admin/costs" element={<AdminRoute><CostsPage /></AdminRoute>} />
                <Route path="admin/ocr-inbox" element={<RoleRoute roles={['admin','kierownik']}><OcrReviewInboxPage /></RoleRoute>} />
                <Route path="admin/archive-center" element={<AdminRoute><ArchiveCenterPage /></AdminRoute>} />
                <Route path="admin/invoices-qa" element={<AdminRoute><InvoicesQAPage /></AdminRoute>} />
              </Route>
            </Routes>
            </Suspense>
            </TasksProvider>
          </ChatProvider>
        </RefreshProvider>
      </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
